import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';
import { Input } from './input';
import { Button } from './button';
import { FORMATS } from '../config/prices';

const FormatSelectorPopup = ({
  isOpen,
  onClose,
  onFormatSelect,
  selectedFormat,
  isPortrait
}) => {
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');

  const parseDimensions = (dimensionStr) => {
    const [width, height] = dimensionStr.split('x').map(d => parseFloat(d.replace(',', '.')));
    const dimensions = {
      width: Number(width),
      height: Number(height),
      originalWidth: Number(width),
      originalHeight: Number(height)
    };

    // When in portrait, the width becomes the height and vice versa
    return isPortrait ? {
      width: dimensions.width,
      height: dimensions.height,
      originalWidth: dimensions.height,  // Swap original dimensions for portrait
      originalHeight: dimensions.width
    } : dimensions;
  };

  const handleFormatSelect = (format) => {
    const dimensions = parseDimensions(FORMATS[format].S);
    onFormatSelect({
      value: format,
      size: 'S',
      label: ` ${format}`,  // Keep only the format ratio
      dimensions: {
        width: dimensions.width,
        height: dimensions.height,
        originalWidth: dimensions.originalWidth,
        originalHeight: dimensions.originalHeight
      },
      aspectRatio: dimensions.width / dimensions.height
    });
    onClose();
  };

  const handleCustomFormat = () => {
    if (!customWidth || !customHeight) return;

    const width = parseFloat(customWidth);
    const height = parseFloat(customHeight);

    // Scale down to max 30cm for largest dimension while preserving aspect ratio
    const scaleFactor = 30 / Math.max(width, height);
    const scaledWidth = Math.round(width * scaleFactor);
    const scaledHeight = Math.round(height * scaleFactor);

    const dimensions = isPortrait ? {
      width: scaledWidth,
      height: scaledHeight,
      originalWidth: height,  // Swap original dimensions for portrait
      originalHeight: width
    } : {
      width: scaledWidth,
      height: scaledHeight,
      originalWidth: width,
      originalHeight: height
    };

    onFormatSelect({
      value: 'custom',
      size: 'S',
      label: ` ${width}x${height}cm`,  // Added space at start
      dimensions,
      aspectRatio: dimensions.width / dimensions.height
    });
    onClose();
  };

  const getDisplayDimensions = (format) => {
    const [width, height] = FORMATS[format].S.split('x').map(d => parseFloat(d.replace(',', '.')));
    return isPortrait ? `${width}x${height}` : `${height}x${width}`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 rounded-2xl flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Select Format</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <Tabs defaultValue="predefined" className="w-full">
            <TabsList className="flex space-x-2 mb-4 w-1/2 mx-auto">
              <TabsTrigger value="predefined" className="flex-1">Format Ratio</TabsTrigger>
              <TabsTrigger value="custom" className="flex-1">Custom Size</TabsTrigger>
            </TabsList>

            <TabsContent value="predefined" className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border-t p-4">
                {Object.keys(FORMATS).map((format) => (
                  <button
                    key={format}
                    className={`p-3 border rounded-xl hover:bg-gray-50 text-center ${selectedFormat?.value === format ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                    onClick={() => handleFormatSelect(format)}
                  >
                    <div className="font-medium">{format}</div>
                    <div className="text-sm text-gray-500">{getDisplayDimensions(format)}cm</div>
                  </button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="custom" className="space-y-6">
              <div className="flex items-center justify-center gap-4 p-4 border-t">
                <Input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(e.target.value)}
                  min="1"
                  max="200"
                  placeholder="Height"
                  className="text-2xl text-center h-12 w-24"
                />

                <span className="text-2xl font-medium text-gray-500">×</span>
                <Input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(e.target.value)}
                  min="1"
                  max="200"
                  placeholder="Width"
                  className="text-2xl text-center h-12 w-24"
                />
                <span className="text-lg text-gray-500">cm</span>
              </div>

              <div className="flex flex-col items-center gap-3">
                <Button
                  onClick={handleCustomFormat}
                  disabled={!customWidth || !customHeight}
                  className="bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl"
                >
                  Apply Custom Size
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default FormatSelectorPopup;